import ProductionAPI from 'api/production.api';
import {
  setTotalFleetAssets,
  setTotalFleetSummary,
  setTotalFleetDetails,
  setIsLoading,
  setFleetPeriodSummary,
  setSuggestions,
  setLoaders,
} from 'store/production.slice';

import { BaseHandler } from './base.handler';
import type { GetProductionReport } from 'types/fuelAnalytics';

export default class ProductionHandler extends BaseHandler {
  private readonly api: ProductionAPI;

  constructor() {
    super();

    this.api = new ProductionAPI();
  }

  async get(
    paramRange: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    initialRun: boolean,
    assetType: string,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ): Promise<any> {
    const range = paramRange === 'Today' ? 'Custom' : paramRange; // we do not need to cache today data
    let stateChanges: any = {
      isLoadingFleetAssets: true,
      isLoadingFleetSummary: true,
      isLoadingSuggestions: true,
    };

    if (initialRun) {
      stateChanges = {
        ...stateChanges,
        isLoadingFleetDetails: true,
        isLoadingFleetPeriodSummary: true,
      };
    }

    this.dispatch(setIsLoading(stateChanges));
    const cacheKey = `production_cache_${range}_${assetType}_${aggregation}_shift${
      shiftWiseAnalysis ? shiftNumber : 'none'
    }`;
    const cachedData = sessionStorage.getItem(cacheKey);

    // If cached data exists and range is not Custom, parse it and return
    if (cachedData && range !== 'Custom') {
      const data = JSON.parse(cachedData);
      // for summary avgLoadPerHour and haul_truck_avgLoadPerHour doesnt return if no value.
      data.fleetSummary.summary.avgLoadPerHour =
        data.fleetSummary.summary.avgLoadPerHour || 0;
      data.fleetSummary.summary.haul_truck_avgLoadPerHour =
        data.fleetSummary.summary.haul_truck_avgLoadPerHour || 0;

      this.dispatch(setTotalFleetSummary(data.fleetSummary));
      this.dispatch(setTotalFleetAssets(data?.assets));
      this.dispatch(setSuggestions(data.suggestions));
      if (initialRun) {
        this.dispatch(setTotalFleetDetails(data.fleetDetails));
        this.dispatch(setFleetPeriodSummary(data.fleetPeriod));
      }
      this.dispatch(
        setIsLoading({
          isLoadingFleetAssets: false,
          isLoadingFleetSummary: false,
          isLoadingFleetDetails: false,
          isLoadingFleetPeriodSummary: false,
          isLoadingSuggestions: false,
        })
      );
      return data.assets; // Return cached assets
    }

    try {
      // Fire off all the API calls without awaiting them
      const fleetSummaryPromise = this.api.getTotalFleetSummary(
        range,
        startDate,
        endDate,
        shouldSendDate,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
        signal
      );

      const fleetAssetsPromise = this.api.getTotalFleet(
        range,
        startDate,
        endDate,
        shouldSendDate,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
        signal
      );

      const suggestionsPromise = this.api.getSuggestions(
        range,
        startDate,
        endDate,
        shouldSendDate,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
        signal
      );

      let fleetDetailsPromise, fleetPeriodPromise;
      if (initialRun) {
        fleetDetailsPromise = this.api.getTotalFleetProduction(
          assetType as 'all' | 'hauler' | 'loader',
          shiftWiseAnalysis,
          shiftNumber,
          signal
        );
        fleetPeriodPromise = this.api.getFleetPeriodSummary(
          shiftWiseAnalysis,
          shiftNumber,
          signal
        );
      }

      if (initialRun) {
        fleetDetailsPromise
          ?.then((totalFleetDetails) => {
            this.dispatch(
              setTotalFleetDetails(totalFleetDetails.totalFleetDetails)
            );
            this.dispatch(
              setIsLoading({
                isLoadingFleetDetails: false,
              })
            );
          })
          .catch(() => {
            this.handleError('Error fetching fleet details');
            this.dispatch(
              setIsLoading({
                isLoadingFleetDetails: false,
              })
            );
          });

        fleetPeriodPromise
          ?.then((fleetPeriod) => {
            this.dispatch(setFleetPeriodSummary(fleetPeriod.data));
            this.dispatch(
              setIsLoading({
                isLoadingFleetPeriodSummary: false,
              })
            );
          })
          .catch(() => {
            this.handleError('Error fetching fleet period summary');
            this.dispatch(
              setIsLoading({
                isLoadingFleetPeriodSummary: false,
              })
            );
          });
      }

      const fleetSummary = await fleetSummaryPromise;
      this.dispatch(setTotalFleetSummary(fleetSummary));
      this.dispatch(
        setIsLoading({
          isLoadingFleetSummary: false,
        })
      );

      const suggestions = await suggestionsPromise;
      this.dispatch(setSuggestions(suggestions));
      this.dispatch(
        setIsLoading({
          isLoadingSuggestions: false,
        })
      );

      const assets = await fleetAssetsPromise
        .then((fleetAssets) => {
          this.dispatch(setTotalFleetAssets(fleetAssets?.assets));
          this.dispatch(
            setIsLoading({
              isLoadingFleetAssets: false,
            })
          );
          return fleetAssets.assets;
        })
        .catch(() => {
          this.handleError('Error fetching fleet assets');
          this.dispatch(
            setIsLoading({
              isLoadingFleetAssets: false,
            })
          );
          return {};
        });

      // Store the results in session storage
      const dataToCache = {
        fleetSummary,
        suggestions,
        fleetDetails: initialRun ? await fleetDetailsPromise : null,
        fleetPeriod: initialRun ? await fleetPeriodPromise : null,
        assets,
      };

      if (paramRange !== 'Today') {
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
        } catch (e) {
          console.log('Error setting cache for production');
        }
      }

      return assets; // Return fetched assets
    } catch (_) {
      this.handleError('An error occurred while fetching production details');
      this.dispatch(
        setIsLoading({
          isLoadingFleetAssets: false,
          isLoadingFleetSummary: false,
          isLoadingFleetDetails: false,
          isLoadingFleetPeriodSummary: false,
          isLoadingSuggestions: false,
        })
      );
    }

    return {};
  }

  async getLoaderDetails(
    paramRange: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    initialRun: boolean,
    assetType: string,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1,
    signal?: AbortSignal
  ): Promise<any> {
    try {
      const range = paramRange === 'Today' ? 'Custom' : paramRange; // we do not need to cache today data
      this.dispatch(setIsLoading({ isLoadingLoaders: true }));

      const cacheKey = `production_loader_details_cache_${range}_${assetType}_${aggregation}_shift${
        shiftWiseAnalysis ? shiftNumber : 'none'
      }`;
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData && range !== 'Custom') {
        try {
          const data = JSON.parse(cachedData);
          this.dispatch(setLoaders(data.loaders || []));
        } catch (e) {
          console.warn('Error parsing cached loader data:', e);
        }

        this.dispatch(setIsLoading({ isLoadingLoaders: false }));
        return JSON.parse(cachedData)?.loaders || [];
      }

      const loaderDetails = await this.api.getProductionLoaders(
        range,
        startDate,
        endDate,
        shouldSendDate,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber,
        signal
      );

      this.dispatch(setLoaders(loaderDetails));
      this.dispatch(setIsLoading({ isLoadingLoaders: false }));

      // Store the results in session storage
      if (paramRange !== 'Today') {
        try {
          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({ loaders: loaderDetails })
          );
        } catch (e) {
          console.warn('Error setting cache for production:', e);
        }
      }
      return loaderDetails;
    } catch (error) {
      this.handleError(
        'An error occurred while fetching production loader details'
      );
      this.dispatch(setIsLoading({ isLoadingLoaders: false }));
      return {};
    }
  }

  async getSuggestions(
    paramRange: string,
    startDate: string,
    endDate: string,
    shouldSendDate: boolean,
    aggregation: string,
    shiftWiseAnalysis: boolean = false,
    shiftNumber: number = 1
  ): Promise<any> {
    try {
      const suggestions = await this.api.getSuggestions(
        paramRange,
        startDate,
        endDate,
        shouldSendDate,
        aggregation,
        shiftWiseAnalysis,
        shiftNumber
      );
      return suggestions;
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      return [];
    }
  }

  async getProductionReport(params: GetProductionReport) {
    try {
      const response = await this.api.getProductionReport(params);
      return response;
    } catch (error) {
      console.error('Error fetching fuel analytics report:', error);
      throw error;
    }
  }
}
